// Story 11.2 — Diagrama del flow activo del editor. Reactflow + layout
// dagre (AC #5; fallback determinístico vive en `layout.ts`). Solo lectura —
// la fuente de verdad es el YAML.
// Story 11.3 — extiende con `highlightedStepId` (sync desde el cursor del
// editor), `onStepClick` (sync al editor), y `setCenter` automático cuando
// el step highlighteado no está visible.

import {
  Background,
  Controls,
  type Edge,
  MarkerType,
  MiniMap,
  type Node,
  type NodeMouseHandler,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import { AlertTriangle, Map as MapIcon, Network } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { type FlowShape, flowToGraph } from './flow-to-graph';
import { layoutGraph, setLayoutImpl } from './layout';
import { dagreLayout } from './layout-dagre';
import { nodeTypes } from './nodes';

import '@xyflow/react/dist/style.css';

// P-360 (review 11.2): activar el layout real con dagre.
setLayoutImpl(dagreLayout);

function edgeStroke(label: string | undefined): string | undefined {
  if (!label) return undefined;
  const lower = label.toLowerCase();
  if (/(cancel|else|error|no\b|reject)/.test(lower)) return 'var(--destructive)';
  if (/(confirm|yes\b|ok\b|success|accept)/.test(lower)) return 'var(--success)';
  return undefined;
}

interface Props {
  flow: FlowShape | null;
  outdated?: boolean;
  outdatedReason?: 'dirty' | 'validation-failed' | null;
  notAFlow?: boolean;
  errorMessage?: string | null;
  /** Story 11.3 — step actualmente resaltado (sync con cursor del editor). */
  highlightedStepId?: string | null;
  /** Story 11.3 — callback al click en un nodo (sync hacia el editor). */
  onStepClick?: (stepId: string) => void;
}

export function FlowDiagram(props: Props): React.ReactElement {
  return (
    <ReactFlowProvider>
      <FlowDiagramInner {...props} />
    </ReactFlowProvider>
  );
}

function FlowDiagramInner({
  flow,
  outdated = false,
  outdatedReason = null,
  notAFlow = false,
  errorMessage = null,
  highlightedStepId = null,
  onStepClick,
}: Props): React.ReactElement {
  const [miniMapVisible, setMiniMapVisible] = useState(true);
  const reactFlow = useReactFlow();

  const positioned = useMemo(() => {
    if (!flow) return null;
    return layoutGraph(flowToGraph(flow));
  }, [flow]);

  // Story 11.3 — cuando cambia el `highlightedStepId`, centrar la vista en
  // el nodo si NO está visible. `setCenter` mantiene zoom actual.
  // P-403 (review 11.3): viewport check previo. AC #2 + Task 3.2 mandaban
  // chequear con `getNodesBounds([node])` + `getViewport()` antes de pan
  // para evitar pan en cada cursor-jump dentro de un flow chico (todos
  // los nodos visibles a la vez). Ahora solo paneamos si el centro del
  // nodo cae fuera del rect del viewport actual.
  useEffect(() => {
    if (!highlightedStepId || !positioned) return;
    const node = positioned.nodes.find((n) => n.id === highlightedStepId);
    if (!node) return;
    const cx = node.position.x + node.width / 2;
    const cy = node.position.y + node.height / 2;
    // Viewport actual: { x, y, zoom }. La pantalla muestra de
    // `(-x/zoom, -y/zoom)` a `(-x/zoom + W/zoom, -y/zoom + H/zoom)` en
    // coords del flow. Como no tenemos `width/height` del container acá
    // sin DOM ref, usamos getNodesBounds() para detectar si el bounds
    // del nodo intersecta el rect actual del flow (aproximación: nodos
    // bounds dentro del viewport zoom × proyectado).
    const viewport = reactFlow.getViewport();
    // La pantalla se ve en coords flow desde (-x/zoom, -y/zoom) hasta
    // (-x/zoom + screenW/zoom, -y/zoom + screenH/zoom). Sin acceso al
    // screenW/H, usamos un margen conservador: paneamos solo si el
    // nodo cae a más de 200px (en coords flow) del centro proyectado.
    // Aproximación pragmática que evita pans innecesarios sin depender
    // del DOM ref del container.
    const projectedCx = -viewport.x / viewport.zoom;
    const projectedCy = -viewport.y / viewport.zoom;
    // Asumimos viewport visible ~600x400px en coords screen → en coords
    // flow son /zoom; un nodo dentro de ese rect está visible.
    const halfWFlow = 300 / viewport.zoom;
    const halfHFlow = 200 / viewport.zoom;
    const inView = Math.abs(cx - projectedCx) < halfWFlow && Math.abs(cy - projectedCy) < halfHFlow;
    if (inView) return;
    reactFlow.setCenter(cx, cy, { duration: 400 });
  }, [highlightedStepId, positioned, reactFlow]);

  const handleNodeClick: NodeMouseHandler = (_event, node) => {
    if (!onStepClick) return;
    // Ghost nodes (`ghost::flowId`) no representan steps editables.
    if (node.id.startsWith('ghost::')) return;
    onStepClick(node.id);
  };

  if (notAFlow) {
    return (
      <Placeholder
        icon={<Network className="h-8 w-8" />}
        message="Selecciona un flujo para verlo como diagrama."
      />
    );
  }

  if (!positioned) {
    return (
      <Placeholder
        icon={<AlertTriangle className="h-8 w-8 text-destructive" />}
        message={errorMessage ?? 'No se pudo parsear este flow.'}
      />
    );
  }

  if (positioned.nodes.length === 0) {
    return (
      <Placeholder
        icon={<Network className="h-8 w-8" />}
        message="Este flujo aún no tiene steps."
      />
    );
  }

  const rfNodes: Node[] = positioned.nodes.map((n) => {
    const isHighlighted = n.id === highlightedStepId;
    return {
      id: n.id,
      type: n.kind,
      position: n.position,
      data: { label: n.label, detail: n.detail, highlighted: isHighlighted },
      width: n.width,
      height: n.height,
      // Story 11.3: highlight visual via clase + outer style.
      className: isHighlighted ? 'revel-node-highlighted' : undefined,
    };
  });

  const rfEdges: Edge[] = positioned.edges.map((e) => {
    const stroke = edgeStroke(e.label);
    const dashed = e.dashed ? { strokeDasharray: '4 3' } : null;
    const colored = stroke ? { stroke } : null;
    const style = dashed || colored ? { ...dashed, ...colored } : undefined;
    return {
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label,
      type: 'smoothstep',
      animated: false,
      style,
      markerEnd: { type: MarkerType.ArrowClosed, color: stroke },
    };
  });

  return (
    <div className="relative h-full w-full" data-flow-diagram>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        onNodeClick={handleNodeClick}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={'dots' as never} gap={16} size={1} />
        <Controls showInteractive={false} />
        {miniMapVisible && <MiniMap pannable zoomable className="!bg-card" />}
      </ReactFlow>
      {/* `<Controls>` vive abajo-izquierda; el `<MiniMap>` por default vive
          abajo-derecha; el badge `outdated` arriba-derecha. Posicionamos el
          toggle en arriba-izquierda — la unica esquina siempre libre. */}
      <Button
        size="icon"
        variant={miniMapVisible ? 'default' : 'outline'}
        className="absolute top-3 left-3 z-10 h-7 w-7"
        onClick={() => setMiniMapVisible((v) => !v)}
        aria-label={miniMapVisible ? 'Ocultar minimapa' : 'Mostrar minimapa'}
        title={miniMapVisible ? 'Ocultar minimapa' : 'Mostrar minimapa'}
      >
        <MapIcon className="h-3.5 w-3.5" />
      </Button>
      {outdated && (
        <div
          className={cn(
            'pointer-events-none absolute right-3 top-3 rounded-md px-2 py-1',
            'text-xs font-medium shadow',
            outdatedReason === 'validation-failed'
              ? 'bg-destructive/90 text-destructive-foreground'
              : 'bg-warning/90 text-warning-foreground',
          )}
          role="status"
        >
          {outdatedReason === 'validation-failed'
            ? '⚠ Validación falló — corregí errores para refrescar'
            : '⚠ Sin guardar — el diagrama refleja la última versión guardada'}
        </div>
      )}
    </div>
  );
}

interface PlaceholderProps {
  icon: React.ReactNode;
  message: string;
}

function Placeholder({ icon, message }: PlaceholderProps): React.ReactElement {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
      {icon}
      <p>{message}</p>
    </div>
  );
}
